/**
 * Token Management Utility
 *
 * Provides centralized token management for both localStorage and React state.
 * Ensures consistency between localStorage operations and component state.
 */

import { useState, useEffect, useCallback } from 'react';

const AUTH_TOKEN_KEY = 'auth-token';
const AUTH_DATA_KEY = 'authData';

// Global variable to store URL token during app initialization
let urlToken: string | null = null;

export interface TokenManager {
  getToken: () => string | null;
  setToken: (token: string) => void;
  removeToken: () => void;
  hasToken: () => boolean;
}

export interface AuthData {
  user: Api.UserObject;
  token: string;
}

export interface AuthDataManager {
  getAuthData: () => AuthData | null;
  setAuthData: (authData: AuthData) => void;
  removeAuthData: () => void;
  hasAuthData: () => boolean;
}

// Store all URL parameters at initialization to prevent race conditions
// Store as string to make it truly immutable - components get fresh URLSearchParams copies
let initialUrlParams: string | null = null;

/**
 * Initialize URL token parsing - must be called before any other operations
 * This ensures URL token takes priority over localStorage token
 */
export function initializeUrlToken(): string | null {
  if (urlToken !== null) {
    return urlToken; // Already parsed
  }

  // Store initial URL parameters to prevent race conditions
  // Store as immutable string - components will get fresh URLSearchParams copies
  if (initialUrlParams === null) {
    initialUrlParams = window.location.search;
  }

  const urlParams = new URLSearchParams(initialUrlParams);
  const jwtToken = urlParams.get('jwt');

  if (jwtToken) {
    urlToken = jwtToken;
    // Immediately set in localStorage to prevent race conditions
    localStorage.setItem(AUTH_TOKEN_KEY, jwtToken);

    // SECURITY: Immediately remove JWT from URL after capturing it
    urlParams.delete('jwt');
    const newUrl = urlParams.toString()
      ? `${window.location.pathname}?${urlParams.toString()}`
      : window.location.pathname;
    window.history.replaceState(null, '', newUrl);
  }

  return urlToken;
}

/**
 * Get initial URL parameters - prevents race conditions during app initialization
 * Returns a NEW URLSearchParams instance each time to prevent mutations
 */
export function getInitialUrlParams(): URLSearchParams {
  if (initialUrlParams === null) {
    // Fallback to current URL if not initialized (shouldn't happen in normal flow)
    initialUrlParams = window.location.search;
  }
  // Always return a fresh URLSearchParams instance to prevent mutations
  return new URLSearchParams(initialUrlParams);
}

/**
 * Clear URL token after authentication is complete
 */
export function clearUrlToken(): void {
  urlToken = null;
}

/**
 * Token Manager - handles localStorage operations for auth tokens
 */
export const tokenManager: TokenManager = {
  getToken: (): string | null => {
    // Prioritize URL token if available
    if (urlToken) {
      return urlToken;
    }

    const localStorageToken = localStorage.getItem(AUTH_TOKEN_KEY);
    return localStorageToken;
  },

  setToken: (token: string): void => {
    localStorage.setItem(AUTH_TOKEN_KEY, token);
    // Clear URL token when setting a new token
    urlToken = null;
  },

  removeToken: (): void => {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    urlToken = null;
  },

  hasToken: (): boolean => {
    const hasUrlToken = !!urlToken;
    const hasLocalStorageToken = !!localStorage.getItem(AUTH_TOKEN_KEY);
    const hasAnyToken = hasUrlToken || hasLocalStorageToken;

    return hasAnyToken;
  },
};

/**
 * Auth Data Manager - handles localStorage operations for auth data
 */
export const authDataManager: AuthDataManager = {
  getAuthData: (): AuthData | null => {
    try {
      const stored = localStorage.getItem(AUTH_DATA_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  },

  setAuthData: (authData: AuthData): void => {
    localStorage.setItem(AUTH_DATA_KEY, JSON.stringify(authData));
  },

  removeAuthData: (): void => {
    localStorage.removeItem(AUTH_DATA_KEY);
  },

  hasAuthData: (): boolean => {
    return !!localStorage.getItem(AUTH_DATA_KEY);
  },
};

/**
 * Combined Token Operations
 *
 * These functions handle both token and auth data together for consistency
 */

/**
 * Store authentication data (both token and user data)
 */
export function storeAuthData(authData: AuthData): void {
  tokenManager.setToken(authData.token);
  authDataManager.setAuthData(authData);
}

/**
 * Clear all authentication data
 */
export function clearAuthData(): void {
  tokenManager.removeToken();
  authDataManager.removeAuthData();
}

/**
 * Get current authentication state
 */
export function getCurrentAuthState(): {
  token: string | null;
  authData: AuthData | null;
  isAuthenticated: boolean;
} {
  const token = tokenManager.getToken();
  const authData = authDataManager.getAuthData();

  return {
    token,
    authData,
    isAuthenticated: !!(token && authData?.user?.is_client),
  };
}

/**
 * React Hook for Token Management
 *
 * Provides a hook that keeps React state in sync with localStorage
 */
export function useTokenManager() {
  const [token, setTokenState] = useState<string | null>(null);
  const [authData, setAuthDataState] = useState<AuthData | null>(null);

  // Initialize state from localStorage
  useEffect(() => {
    setTokenState(tokenManager.getToken());
    setAuthDataState(authDataManager.getAuthData());
  }, []);

  // Synchronized token setter
  const setToken = useCallback((newToken: string) => {
    tokenManager.setToken(newToken);
    setTokenState(newToken);
  }, []);

  // Synchronized token remover
  const removeToken = useCallback(() => {
    tokenManager.removeToken();
    setTokenState(null);
  }, []);

  // Synchronized auth data setter
  const setAuthData = useCallback((newAuthData: AuthData) => {
    storeAuthData(newAuthData);
    setTokenState(newAuthData.token);
    setAuthDataState(newAuthData);
  }, []);

  // Synchronized auth data remover
  const removeAuthData = useCallback(() => {
    clearAuthData();
    setTokenState(null);
    setAuthDataState(null);
  }, []);

  return {
    token,
    authData,
    setToken,
    removeToken,
    setAuthData,
    removeAuthData,
    isAuthenticated: !!(token && authData?.user?.is_client),
  };
}

/**
 * Legacy compatibility - maintains existing getAuthToken function
 * @deprecated Use tokenManager.getToken() instead
 */
export const getAuthToken = tokenManager.getToken;
